import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import { PageState } from '../../../Types/page-state.type';

@Component({
  selector: 'app-connect-intune',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './connect-intune.component.html',
  styleUrl: './connect-intune.component.scss',
})
export class ConnectIntuneComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly onboardingFacade = inject(OnboardingFacade);
  private readonly translate = inject(TranslateService);

  pageState = signal<PageState>('verifying');
  errorMessage = signal<string | null>(null);
  consentGranted = signal(false);
  isConsentLoading = signal(false);

  requiredPermissions: string[] = [];

  ngOnInit(): void {
    this.loadRequiredPermissions();
    this.resolveEntryState();
  }

  private loadRequiredPermissions(): void {
    this.requiredPermissions = this.onboardingFacade.graphPermissions();
  }

  private resolveEntryState(): void {
    const params = this.route.snapshot.queryParamMap;
    const consent = params.get('consent');
    const consentError = params.get('error');

    if (consentError) {
      this.errorMessage.set(decodeURIComponent(consentError));
      this.pageState.set('error_consent');
      return;
    }

    if (consent === 'success') {
      this.handleConsentSuccess();
      return;
    }

    this.checkPlanSelected();
  }

  private handleConsentSuccess(): void {
    this.consentGranted.set(true);
    this.onboardingFacade.get({ skipLoading: true }).subscribe({
      next: (onboarding) => {
        this.consentGranted.set(!!onboarding?.admin_consent_granted_at);
        this.pageState.set('ready');
      },
      error: () => {
        this.pageState.set('ready');
      },
    });
  }

  private checkPlanSelected(): void {
    this.onboardingFacade.get({ skipLoading: true }).subscribe({
      next: (onboarding) => {
        if (!onboarding?.plan_data) {
          this.router.navigate(['/setup/plan-selection']);
          return;
        }

        this.consentGranted.set(!!onboarding.admin_consent_granted_at);
        this.pageState.set('ready');
      },
      error: () => {
        this.router.navigate(['/setup/plan-selection']);
      },
    });
  }

  permissionTooltip(permission: string): string {
    const key =
      'onboarding.setup.connectIntune.permissionTooltips.' + permission.split('.').join('_');
    const resolved = this.translate.instant(key);

    return resolved === key
      ? this.translate.instant('onboarding.setup.connectIntune.permissionTooltip')
      : resolved;
  }

  onContinue(): void {
    this.router.navigate(['/setup/review']);
  }

  onGrantConsent(): void {
    this.isConsentLoading.set(true);
    this.errorMessage.set(null);

    this.onboardingFacade.getAdminConsentUrl().subscribe({
      next: (response) => {
        window.location.href = response.data;
      },
      error: (error: Error) => {
        this.isConsentLoading.set(false);
        this.errorMessage.set(
          error.message ||
            this.translate.instant('onboarding.setup.connectIntune.errors.consentFailed')
        );
        this.pageState.set('error_consent');
      },
    });
  }

  onBack(): void {
    this.router.navigate(['/setup/licences']);
  }
}
